import {
  viewContract, signContract, fetchContractFile, getKycStatus, uploadKyc,
  ApiError, type ViewResponse, type SignResponse, type KycStatus,
} from './api';

const CONSENT_TEXT =
  'I have reviewed this document, agree to its terms, and understand that checking this box and clicking “Sign document” creates my electronic signature.';
const BUTTON_TEXT = 'Sign document';
const KYC_CONSENT_TEXT =
  'I consent to submitting my identity documents to the party that requested this signature for verification, and confirm the details and images I provide are accurate and my own.';

// Backend messages (contracts.service.ts's resolveExternalRequest/externalSign) that mean the
// link is DONE, not retryable — switch to the terminal error view instead of an inline form error.
const TERMINAL_PATTERNS = [/already been signed/i, /expired/i, /revoked/i, /voided/i, /invalid.*signing link/i];

const content = document.getElementById('content') as HTMLElement;

/* ------------------------------------------------------------------------------------------
 * tiny DOM helpers — no framework needed for one form
 * ---------------------------------------------------------------------------------------- */
function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const child of children) node.append(child); // .append() text-nodes strings — never innerHTML, everything here may be server- or user-supplied
  return node;
}

function clear(node: HTMLElement) {
  node.replaceChildren();
}

/* ------------------------------------------------------------------------------------------
 * token extraction — fragment-first (never sent to the server / never logged), with a
 * path-based fallback for the CURRENT externalInvite() link shape. See README.md's "known
 * gaps" section: the real link minted today is `${base}/s/<token>` (path, not fragment), so
 * that fallback is what actually makes today's links work — the fragment-first parsing here
 * is ready for a future externalInvite() change to `${base}/s/#<token>`.
 * ---------------------------------------------------------------------------------------- */
function extractToken(): string | null {
  const hash = location.hash.replace(/^#\/?/, '');
  if (hash) {
    const parts = hash.split('/').filter(Boolean);
    const candidate = parts.length > 1 && parts[0] === 's' ? parts[1] : parts[0];
    if (candidate) return candidate;
  }
  const pathParts = location.pathname.split('/').filter(Boolean);
  if (pathParts.length >= 2 && pathParts[0] === 's') return pathParts[1];
  return null;
}

/* ------------------------------------------------------------------------------------------
 * render states
 * ---------------------------------------------------------------------------------------- */
function renderLoading() {
  clear(content);
  content.append(
    h('div', { class: 'center' }, [
      h('span', { class: 'spinner', 'aria-hidden': 'true' }, []),
      h('p', {}, ['Loading your document…']),
    ]),
  );
}

function renderError(message: string) {
  clear(content);
  content.append(
    h('div', { class: 'card center' }, [
      h('p', { class: 'card-title' }, [message]),
      h('p', { class: 'hint' }, ['If you believe this is a mistake, contact whoever sent you this link.']),
    ]),
  );
}

function renderSuccess(receipt: SignResponse) {
  clear(content);
  const time = new Date(receipt.trustedTimestamp.time);
  const timeStr = Number.isNaN(time.getTime()) ? receipt.trustedTimestamp.time : time.toLocaleString();

  content.append(
    h('div', { class: 'card center' }, [
      h('div', { class: 'success-icon', 'aria-hidden': 'true' }, ['✓']),
      h('p', { class: 'card-title' }, ['Signed and sealed']),
      h('p', { class: 'hint' }, ['Your electronic signature has been recorded. You can close this page.']),
      h('dl', { class: 'receipt' }, [
        h('dt', {}, ['Signed']),
        h('dd', {}, [timeStr]),
        h('dt', {}, ['Document hash']),
        h('dd', {}, [receipt.documentHash]),
        h('dt', {}, ['Seal']),
        h('dd', {}, [`${receipt.seal.alg} · ${receipt.seal.keyId}`]),
      ]),
    ]),
  );
}

function renderView(token: string, data: ViewResponse) {
  clear(content);

  const docCard = h('div', { class: 'card' }, [
    h('h1', { class: 'doc-title' }, [data.title || 'Untitled document']),
    h('p', { class: 'doc-meta' }, [`Addressed to ${data.name ? `${data.name} · ` : ''}${data.email}`]),
    h('pre', { class: 'doc-body' }, [
      data.content || (data.fileUrl ? 'This agreement is provided as an attached file — see below.' : 'No content available.'),
    ]),
  ]);
  if (data.fileUrl) {
    // The attachment lives behind IAP on the main API; fetch it through this service's byte proxy
    // (POST /api/file, token-authorized) and hand the signer a download. We ALWAYS force a download
    // (blob → <a download>), never inline-render, so a hostile attachment type can't execute in this
    // origin — mirrors the main API's INLINE_SAFE_EXTS attachment forcing, defense in depth.
    const fileNote = h('div', { class: 'notice' }, ['This agreement references an attached file.']);
    const dlBtn = h('button', { class: 'btn-secondary', type: 'button' }, ['Download the attached document']);
    dlBtn.addEventListener('click', async () => {
      dlBtn.setAttribute('disabled', 'true');
      try {
        const { blob, filename } = await fetchContractFile(token);
        const url = URL.createObjectURL(blob);
        const a = h('a', { href: url, download: filename });
        a.style.display = 'none';
        document.body.append(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      } catch (e) {
        fileNote.append(h('p', { class: 'hint' }, [e instanceof ApiError ? e.message : 'Could not load the file.']));
      } finally {
        dlBtn.removeAttribute('disabled');
      }
    });
    fileNote.append(dlBtn);
    docCard.append(fileNote);
  }
  content.append(docCard);

  // Identity gate: when the issuer required KYC, the signer must be VERIFIED before the sign form is
  // shown. The backend enforces this too (externalSign refuses without a VERIFIED record) — this is
  // the UX side of the same gate, not the security boundary.
  if (data.kycRequired && data.kycStatus !== 'VERIFIED') {
    renderKycGate(token, data);
    return;
  }

  const typedNameInput = h('input', {
    class: 'input', type: 'text', placeholder: 'Full legal name', autocomplete: 'name', maxlength: '120',
  });
  const consentCheckbox = h('input', { type: 'checkbox' });
  const errorEl = h('p', { class: 'error-text' }, []);
  errorEl.hidden = true;
  const submitBtn = h('button', { class: 'btn btn-primary', type: 'submit' }, [BUTTON_TEXT]);

  const showError = (msg: string) => { errorEl.textContent = msg; errorEl.hidden = false; };
  const hideError = () => { errorEl.hidden = true; errorEl.textContent = ''; };

  const form = h('form', { class: 'card' }, [
    h('p', { class: 'card-title' }, ['Sign this agreement']),
    h('div', { class: 'field' }, [h('span', { class: 'label' }, ['Your full legal name']), typedNameInput]),
    h('label', { class: 'consent-row' }, [consentCheckbox, h('span', {}, [CONSENT_TEXT])]),
    errorEl,
    submitBtn,
  ]);
  content.append(form);

  const setBusy = (busy: boolean) => {
    submitBtn.disabled = busy;
    typedNameInput.disabled = busy;
    consentCheckbox.disabled = busy;
    submitBtn.replaceChildren();
    if (busy) submitBtn.append(h('span', { class: 'spinner', 'aria-hidden': 'true' }, []), document.createTextNode(' Signing…'));
    else submitBtn.append(BUTTON_TEXT);
  };

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    hideError();

    const typedName = typedNameInput.value.trim();
    if (typedName.length < 2) { showError('Please type your full legal name.'); return; }
    if (!consentCheckbox.checked) { showError('Please tick the box to sign.'); return; }

    setBusy(true);
    signContract(token, typedName, CONSENT_TEXT)
      .then((receipt) => renderSuccess(receipt))
      .catch((e: unknown) => {
        const message = e instanceof ApiError ? e.message : 'Signing failed. Please try again.';
        if (TERMINAL_PATTERNS.some((re) => re.test(message))) { renderError(message); return; }
        setBusy(false);
        showError(message);
      });
  });
}

/* ------------------------------------------------------------------------------------------
 * KYC identity gate — shown before the sign form when the issuer required verification
 * ---------------------------------------------------------------------------------------- */
function renderKycGate(token: string, data: ViewResponse) {
  // Render the gate BELOW the document card (renderView already appended it) into a dedicated host,
  // so the signer keeps seeing / can download what they're being asked to sign while they verify.
  // Only this host is swapped between the upload form and the pending state — never the document.
  const host = h('div', { class: 'kyc-host' }, []);
  content.append(host);
  const status: KycStatus = data.kycStatus ?? 'NOT_STARTED';
  if (status === 'PENDING') { renderKycPending(token, host); return; }
  // NOT_STARTED / REJECTED / anything else → the upload form (with the reviewer's note when rejected)
  renderKycForm(token, data, host, status === 'REJECTED' ? (data.kycNote ?? null) : null);
}

function fileField(label: string, hint?: string): { row: HTMLElement; input: HTMLInputElement } {
  const input = h('input', { class: 'input', type: 'file', accept: 'image/*,.pdf' }) as HTMLInputElement;
  const row = h('div', { class: 'field' }, [
    h('span', { class: 'label' }, [label]),
    input,
    ...(hint ? [h('span', { class: 'hint' }, [hint])] : []),
  ]);
  return { row, input };
}

function renderKycForm(token: string, data: ViewResponse, host: HTMLElement, rejectionNote: string | null) {
  clear(host);
  const docTypeSel = h('select', { class: 'input' }, [
    h('option', { value: 'PASSPORT' }, ['Passport']),
    h('option', { value: 'ID_CARD' }, ['National ID card']),
    h('option', { value: 'DRIVERS_LICENSE' }, ["Driver's licence"]),
  ]) as HTMLSelectElement;
  const legalNameInput = h('input', { class: 'input', type: 'text', placeholder: 'Full legal name', autocomplete: 'name', maxlength: '120' }) as HTMLInputElement;
  if (data.name) legalNameInput.value = data.name;
  const docNumberInput = h('input', { class: 'input', type: 'text', placeholder: 'Document number (optional)', maxlength: '60' }) as HTMLInputElement;
  const dobInput = h('input', { class: 'input', type: 'date' }) as HTMLInputElement;

  const front = fileField('Photo of your ID (front)', 'Clear photo or scan — JPG, PNG or PDF, up to 10 MB.');
  const back = fileField('Back of your ID (optional)', 'For a national ID card or driving licence.');
  const selfie = fileField('A selfie holding your ID (optional)', 'Helps the reviewer match your face to the document.');

  const consentCheckbox = h('input', { type: 'checkbox' }) as HTMLInputElement;
  const errorEl = h('p', { class: 'error-text' }, []);
  errorEl.hidden = true;
  const submitBtn = h('button', { class: 'btn btn-primary', type: 'submit' }, ['Submit for verification']);

  const showError = (msg: string) => { errorEl.textContent = msg; errorEl.hidden = false; };
  const hideError = () => { errorEl.hidden = true; errorEl.textContent = ''; };

  const intro = rejectionNote
    ? h('div', { class: 'notice' }, [`Your previous submission was not accepted${rejectionNote ? `: ${rejectionNote}` : '.'} Please try again.`])
    : h('p', { class: 'hint' }, ['The party requesting your signature asks you to verify your identity before signing. Your documents are shared only with them.']);

  const form = h('form', { class: 'card' }, [
    h('p', { class: 'card-title' }, ['Verify your identity']),
    intro,
    h('div', { class: 'field' }, [h('span', { class: 'label' }, ['Document type']), docTypeSel]),
    h('div', { class: 'field' }, [h('span', { class: 'label' }, ['Full legal name']), legalNameInput]),
    h('div', { class: 'field' }, [h('span', { class: 'label' }, ['Document number']), docNumberInput]),
    h('div', { class: 'field' }, [h('span', { class: 'label' }, ['Date of birth (optional)']), dobInput]),
    front.row,
    back.row,
    selfie.row,
    h('label', { class: 'consent-row' }, [consentCheckbox, h('span', {}, [KYC_CONSENT_TEXT])]),
    errorEl,
    submitBtn,
  ]);
  host.append(form);

  const setBusy = (busy: boolean) => {
    submitBtn.disabled = busy;
    submitBtn.replaceChildren();
    if (busy) submitBtn.append(h('span', { class: 'spinner', 'aria-hidden': 'true' }, []), document.createTextNode(' Submitting…'));
    else submitBtn.append('Submit for verification');
  };

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    hideError();

    const docFile = front.input.files?.[0];
    if (!docFile) { showError('Please attach a photo of your ID.'); return; }
    if (legalNameInput.value.trim().length < 2) { showError('Please enter your full legal name.'); return; }
    if (!consentCheckbox.checked) { showError('Please tick the box to submit your documents.'); return; }

    const fd = new FormData();
    fd.set('document', docFile);
    if (back.input.files?.[0]) fd.set('documentBack', back.input.files[0]);
    if (selfie.input.files?.[0]) fd.set('selfie', selfie.input.files[0]);
    fd.set('documentType', docTypeSel.value);
    fd.set('consentAccepted', 'true');
    fd.set('intake', JSON.stringify({
      legalName: legalNameInput.value.trim(),
      documentNumber: docNumberInput.value.trim() || null,
      dateOfBirth: dobInput.value || null,
    }));

    setBusy(true);
    uploadKyc(token, fd)
      .then(() => renderKycPending(token, host))
      .catch((e: unknown) => {
        const message = e instanceof ApiError ? e.message : 'Could not submit your documents. Please try again.';
        if (TERMINAL_PATTERNS.some((re) => re.test(message))) { renderError(message); return; }
        setBusy(false);
        showError(message);
      });
  });
}

function renderKycPending(token: string, host: HTMLElement) {
  clear(host);
  const checkBtn = h('button', { class: 'btn-secondary', type: 'button' }, ['Check now']);
  host.append(
    h('div', { class: 'card center' }, [
      h('div', { class: 'success-icon', 'aria-hidden': 'true' }, ['⏳']),
      h('p', { class: 'card-title' }, ['Identity under review']),
      h('p', { class: 'hint' }, ['Your documents are with the reviewer. This page updates automatically once they decide — or you can safely close it and reopen your original link later.']),
      checkBtn,
    ]),
  );

  let iv: ReturnType<typeof setInterval>;
  let autoStop: ReturnType<typeof setTimeout>;
  let inFlight = false; // guard overlapping polls (interval tick + manual click) → no double render
  let autoStopped = false; // only the interval honors this; the manual button always works
  const check = async () => {
    if (inFlight) return;
    inFlight = true;
    try {
      const r = await getKycStatus(token);
      if (r.status === 'VERIFIED' || r.status === 'REJECTED') {
        // Re-fetch the view first; commit the terminal transition ONLY if it succeeds, so a transient
        // failure here can't strand the signer on this screen with polling already torn down.
        const d = await viewContract(token);
        clearInterval(iv);
        clearTimeout(autoStop);
        renderView(token, d); // VERIFIED → sign form; REJECTED → upload form with the note
      }
    } catch { /* transient network / rate-limit — stay and keep trying */ }
    finally { inFlight = false; }
  };
  checkBtn.addEventListener('click', () => {
    checkBtn.setAttribute('disabled', 'true');
    void check().finally(() => checkBtn.removeAttribute('disabled'));
  });
  iv = setInterval(() => { if (!autoStopped) void check(); }, 15_000);
  // stop the AUTO poll after ~10 minutes to avoid an endless background loop; "Check now" still works,
  // and reopening the original link (boot re-checks) always resumes.
  autoStop = setTimeout(() => { autoStopped = true; clearInterval(iv); }, 10 * 60 * 1000);
}

/* ------------------------------------------------------------------------------------------
 * boot
 * ---------------------------------------------------------------------------------------- */
async function boot() {
  const token = extractToken();
  if (!token) {
    renderError('This link is missing its signing token. Please use the exact link you were sent.');
    return;
  }
  // Scrub the token from the visible address bar now that it's safely captured — this is a
  // single-use link; a refresh after this point is expected to need the original link again
  // (the token lives only in this closure for the rest of the page's life, never in
  // localStorage/sessionStorage).
  history.replaceState(null, '', '/');

  renderLoading();
  try {
    const data = await viewContract(token);
    renderView(token, data);
  } catch (e) {
    renderError(e instanceof ApiError ? e.message : 'Something went wrong loading this document.');
  }
}

void boot();
